import {FC, useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {DatePicker} from 'antd'
import dayjs, {Dayjs} from 'dayjs'
import {
  PublicExpert,
  PublicExpertiseService,
  bookExpertise,
  getExpertProfile,
  getExpertServices,
} from '../../services/expertiseSearchService'
import {handleApiError} from '../../utils/handleApiError'
import {showErrorAlert} from '../../helpers/alert'
import {ExpertiseOfferCard, rateLabel} from './components/ExpertiseOfferCard'

/**
 * Reservar un expertise (Figma 4457:76983 → 4458:89557 → 4458:96234).
 *
 * Dos etapas en UNA pagina, no dos rutas: la segunda solo agrega campos sobre el servicio ya
 * elegido, y volver atras tiene que conservar la seleccion. Con dos rutas, el "back" del navegador
 * perderia el estado y el comprador tendria que elegir otra vez.
 *
 * Llegando desde la seccion Expertise del buscador ya viene un `?service=`, asi que se salta la
 * primera etapa: ese click YA fue la eleccion.
 */
const Arrow = ({dark}: {dark?: boolean}) => (
  <svg width='13' height='4' viewBox='0 0 13.3502 3.88356' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path d='M8.5 0.379406L12 3.38356H0' stroke={dark ? '#151515' : 'white'} />
  </svg>
)

/**
 * Colchon de respuesta: no se puede agendar antes de 5 dias. El experto necesita esa antelacion,
 * y el backend rechaza cualquier fecha mas cercana — el picker lo refleja para no ofrecer fechas
 * que van a fallar al enviar.
 */
const RESPONSE_BUFFER_DAYS = 5

const BookExpertisePage: FC = () => {
  const {partnerId} = useParams<{partnerId: string}>()
  const history = useHistory()
  const location = useLocation()

  const preselected = useMemo(
    () => new URLSearchParams(location.search).get('service'),
    [location.search]
  )

  const [expert, setExpert] = useState<PublicExpert | null>(null)
  const [services, setServices] = useState<PublicExpertiseService[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [step, setStep] = useState<1 | 2>(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [scheduledOn, setScheduledOn] = useState<Dayjs | null>(null)
  const [hours, setHours] = useState('1')
  const [message, setMessage] = useState('')

  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([getExpertProfile(partnerId), getExpertServices(partnerId)])
      .then(([profile, list]) => {
        if (!alive) return
        setExpert(profile)
        setServices(list)

        const fromUrl = preselected ? list.find((s) => String(s.id) === preselected) : undefined
        if (fromUrl) {
          setSelectedId(fromUrl.id)
          setStep(2)
        }
      })
      .catch((error) => alive && handleApiError(error))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [partnerId, preselected])

  const selected = services.find((s) => s.id === selectedId) || null

  /** El monto que vera el experto: tarifa por hora x horas. Mismo calculo que el backend. */
  const estimate = useMemo(() => {
    if (!selected || selected.rate === null || selected.rate === undefined) return null
    const rate = Number(selected.rate)
    const qty = Number(hours)
    if (isNaN(rate) || isNaN(qty) || qty < 1) return null
    const total = rate * qty
    return `$${total % 1 === 0 ? total.toLocaleString('en-US') : total.toLocaleString('en-US', {minimumFractionDigits: 2})}`
  }, [selected, hours])

  const handleSubmit = async () => {
    if (!selected || saving) return
    if (!scheduledOn) {
      showErrorAlert({title: 'Schedule required', message: 'Pick the date you need this expertise.'})
      return
    }
    const qty = Number(hours)
    if (!qty || qty < 1) {
      showErrorAlert({title: 'Hours required', message: 'Enter at least one hour.'})
      return
    }

    try {
      setSaving(true)
      const created = await bookExpertise(selected.id, {
        scheduledOn: scheduledOn.toISOString(),
        hours: qty,
        message: message.trim() || undefined,
      })
      showErrorAlert({
        title: 'Request sent',
        message: 'The Expert will accept or decline your request. Follow it in Messages.',
      })
      // El hilo del chat nace junto con la solicitud, asi que ahi continua todo el flujo. Se
      // adjunta el uuid como query param — MessagesPage ya soporta ese deep link (?uuid=) y
      // autoselecciona la conversacion recien creada al montar. Si por algun motivo el backend no
      // devolviera el uuid (respuesta parcial, error deserializado), cae al fallback anterior y
      // simplemente abre /messages sin autoselect.
      const conversationUuid = created?.conversationUuid ?? created?.conversation_uuid
      history.push(conversationUuid ? `/messages?uuid=${conversationUuid}` : '/messages')
    } catch (error: any) {
      handleApiError(error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className='members-page__no-results'>Loading...</div>
  if (!expert) return <div className='members-page__no-results'>Expert not found</div>

  return (
    <div className='book-expertise'>
      <span className='book-expertise__eyebrow'>Expertise</span>
      <h1 className='book-expertise__title'>Book Expertise by {expert.companyName}</h1>

      <p className='book-expertise__lead'>
        {step === 1
          ? 'Start your booking by outlining your needs. The Expert will accept or decline your request.'
          : 'Tell the Expert when you need it and for how long.'}
      </p>

      {step === 1 && (
        <div className='expertise-offer-list'>
          {services.map((service) => (
            <ExpertiseOfferCard
              key={service.id}
              service={service}
              selected={service.id === selectedId}
              onSelect={() => setSelectedId(service.id)}
            />
          ))}
        </div>
      )}

      {step === 2 && selected && (
        <>
          <div className='expertise-offer-list'>
            <ExpertiseOfferCard service={selected} selected variant='static' />
          </div>

          <div className='book-expertise__field'>
            <label className='book-expertise__label'>Schedule</label>
            <DatePicker
              className='book-expertise__date'
              value={scheduledOn}
              onChange={setScheduledOn}
              showTime={{format: 'HH:mm'}}
              format='DD MMMM YYYY - HH:mm'
              // El plazo del experto se cuenta HACIA ATRAS desde esta fecha, asi que agendar
              // mas cerca del colchon le dejaria menos margen del acordado.
              disabledDate={(current) =>
                current && current < dayjs().add(RESPONSE_BUFFER_DAYS, 'day').startOf('day')
              }
              popupStyle={{zIndex: 10060}}
              placeholder='Pick a date and time'
            />
            <span className='book-expertise__hint'>
              Earliest available date is {dayjs().add(RESPONSE_BUFFER_DAYS, 'day').format('DD MMMM YYYY')}.
              The Expert has until {RESPONSE_BUFFER_DAYS} days before your date to respond.
            </span>
          </div>

          <div className='book-expertise__field'>
            <label className='book-expertise__label'>Hours needed</label>
            <input
              type='number'
              min='1'
              max='999'
              className='book-expertise__input'
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder='1'
            />
            {/* Se muestra el total para que nadie mande una solicitud sin saber cuanto implica. */}
            <span className='book-expertise__hint'>
              {estimate
                ? `${rateLabel(selected)} ${selected.rateUnit ? `/ ${selected.rateUnit}` : ''} — estimated ${estimate}`
                : 'This expertise is quoted case by case.'}
            </span>
          </div>

          <div className='book-expertise__field'>
            <label className='book-expertise__label'>Message</label>
            <textarea
              className='book-expertise__textarea'
              rows={5}
              maxLength={800}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder='Provide a clear and detailed description of your request.'
            />
          </div>
        </>
      )}

      <div className='book-expertise__actions'>
        <button
          type='button'
          className='book-expertise__btn book-expertise__btn--outline'
          disabled={saving}
          onClick={() => {
            // En la segunda etapa "Cancel" regresa a elegir, salvo que se haya entrado con el
            // servicio ya elegido desde el buscador: ahi no hay etapa 1 a la que volver.
            if (step === 2 && !preselected) setStep(1)
            else history.goBack()
          }}
        >
          Cancel
        </button>

        {step === 1 ? (
          <button
            type='button'
            className='book-expertise__btn book-expertise__btn--solid'
            disabled={!selectedId}
            onClick={() => setStep(2)}
          >
            <span>Next</span>
            <Arrow />
          </button>
        ) : (
          <button
            type='button'
            className='book-expertise__btn book-expertise__btn--solid'
            disabled={saving}
            onClick={handleSubmit}
          >
            <span>{saving ? 'Sending...' : 'Send request'}</span>
            <Arrow />
          </button>
        )}
      </div>
    </div>
  )
}

export {BookExpertisePage}
export default BookExpertisePage
